import { useState } from "react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Field, PasswordField, FormError } from "@/components/auth/AuthShell";
import { Button } from "@/components/ui/button";

const Card = ({ title, subtitle, children, testId }) => (
  <section className="rounded-3xl bg-white border border-slate-200 p-6 sm:p-8 space-y-5" data-testid={testId}>
    <div><h2 className="font-display text-xl font-bold text-slate-900">{title}</h2><p className="text-sm text-slate-500 mt-1">{subtitle}</p></div>
    {children}
  </section>
);

export default function Account() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [savingName, setSavingName] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [pwError, setPwError] = useState("");
  const [savingPw, setSavingPw] = useState(false);

  const saveName = async (e) => {
    e.preventDefault();
    setSavingName(true);
    try {
      const r = await api.put("/auth/profile", { name });
      setUser(r.data);
      toast.success("Name updated");
    } catch (err) { toast.error(errorMessage(err)); } finally { setSavingName(false); }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    setPwError("");
    if (next !== confirm) { setPwError("New passwords do not match"); return; }
    setSavingPw(true);
    try {
      const r = await api.post("/auth/change-password", { current_password: current, new_password: next });
      if (r.data.access_token) setAccessToken(r.data.access_token);
      toast.success("Password changed. Other devices have been signed out.");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) { setPwError(errorMessage(err)); } finally { setSavingPw(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-12 py-10 sm:py-16 space-y-8" data-testid="account-page">
      <div className="space-y-2">
        <p className="eyebrow">Account</p>
        <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">Your settings</h1>
        <p className="text-slate-600">Signed in as <span className="font-medium text-slate-900" data-testid="account-email">{user?.email}</span></p>
      </div>

      <Card title="Display name" subtitle="How we greet you across the app." testId="account-name-card">
        <form onSubmit={saveName} className="space-y-4">
          <Field id="name" label="Name" value={name} onChange={setName} autoComplete="name" testId="account-name-input" />
          <Button type="submit" disabled={savingName || !name.trim() || name.trim() === user?.name} className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="account-name-save-button">Save name</Button>
        </form>
      </Card>

      <Card title="Change password" subtitle="Use at least 8 characters." testId="account-password-card">
        <form onSubmit={savePassword} className="space-y-4">
          <PasswordField id="current" label="Current password" value={current} onChange={setCurrent} autoComplete="current-password" testId="account-current-password-input" />
          <PasswordField id="next" label="New password" value={next} onChange={setNext} autoComplete="new-password" testId="account-new-password-input" />
          <PasswordField id="confirm" label="Confirm new password" value={confirm} onChange={setConfirm} autoComplete="new-password" testId="account-confirm-password-input" />
          <FormError message={pwError} />
          <Button type="submit" disabled={savingPw} className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="account-password-save-button">Change password</Button>
        </form>
      </Card>
    </div>
  );
}
